import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { isAuthenticated, getUserInfo, setCookieAuthVerified, clearExplicitLogout } from '../utils/auth';
import CreditsModal from '../components/CreditsModal';
import StartTrialModal from '../components/StartTrialModal';
import ContactSection from '../components/ContactSection';
import SEO from '../components/SEO';
import { fetchCreditsCached } from '../services/credits';
import { BACKEND_URL } from '../config/api';
import './Welcome.css';
// Import flag icons
import { SA, RU, ES, BY, BD, BG, HK, HR, CZ, DK, NL, GB, EE, FI, FR, DE, GR, IL, IN, HU, IE, IT, ID, JP, KR, LV, LT, MY, MT, CN, MN, NO, IR, PL, PT, RO, SK, SI, KE, SE, PH, TH, TR, PK, UA, VN } from 'country-flag-icons/react/3x2';

// Welsh flag as inline SVG component (not in country-flag-icons)
const WelshFlag = ({ className }) => (
  <svg className={className} viewBox="0 0 3 2" xmlns="http://www.w3.org/2000/svg">
    <rect width="3" height="1" y="0" fill="#00AB39"/>
    <rect width="3" height="1" y="1" fill="#fff"/>
  </svg>
);

function Welcome() {
  const navigate = useNavigate();
  const [isAuth, setIsAuth] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [showTrialModal, setShowTrialModal] = useState(false);
  const [isTrial, setIsTrial] = useState(true);
  const wrapperRef = useRef(null);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const VIDEO_ID = 'uf6ow6IwJPY';
  
  // Track active trial session
  const [trialTimeRemaining, setTrialTimeRemaining] = useState(null);
  const [trialCooldownRemainingMs, setTrialCooldownRemainingMs] = useState(0);
  
  // Trial cooldown constants
  const TRIAL_COOLDOWN_MS = 15 * 60 * 1000; // 15 minutes
  const TRIAL_LAST_END_KEY = 'bk_last_trial_ended_at';

  const quickStats = [
    'Practice with realistic interview scenarios and questions',
    'AI-powered coaching based on your resume and experience',
    'Build confidence with instant feedback and structured prep',
  ];

  const highlights = [
    { title: 'Personalized coaching', text: 'AI analyzes your background and helps you craft compelling answers for any question.' },
    { title: 'Privacy-first practice', text: 'Practice locally with complete privacy. Your data stays on your device.' },
    { title: 'Resume-aware prep', text: 'Upload once. Every practice answer references your actual projects, impact, and experience.' },
    { title: 'Technical + behavioral', text: 'Prepare for coding rounds, system design, leadership questions, and HR conversations.' },
    { title: 'Structured preparation', text: 'Build a library of strong answers before your interview day.' },
  ];

  const steps = [
    { title: 'Upload your resume', text: 'Start by uploading your resume so AI can understand your background, skills, and achievements.' },
    { title: 'Practice common questions', text: 'Work through typical interview questions. AI generates personalized answers based on your experience.' },
    { title: 'Refine and memorize', text: 'Review, edit, and practice your answers until they feel natural and confident.' },
  ];

  const checkAuth = () => {
    const authenticated = isAuthenticated();
    setIsAuth(authenticated);
    if (authenticated) {
      const info = getUserInfo();
      setUserInfo(info);
    } else {
      setUserInfo(null);
    }
  };

  useEffect(() => {
    // Refresh credits when component mounts
    window.dispatchEvent(new CustomEvent('creditsUpdated'));

    // Handle OAuth redirect with query params (?success=true&email=...&name=...)
    // Token is in HttpOnly cookie - no need to read or store it
    const params = new URLSearchParams(window.location.search);
    const success = params.get('success');
    const urlEmail = params.get('email');
    
    if (success === 'true' && urlEmail) {
      const urlName = params.get('name');
      const urlPicture = params.get('pictureUrl');
      const urlUserId = params.get('userId');

      localStorage.setItem('userEmail', decodeURIComponent(urlEmail));

      if (urlName) {
        localStorage.setItem('userName', decodeURIComponent(urlName.replace(/\+/g, ' ')));
      }
      if (urlPicture) {
        localStorage.setItem('userPicture', decodeURIComponent(urlPicture));
      }
      if (urlUserId) {
        localStorage.setItem('userId', urlUserId);
      }

      // Mark cookie auth as active
      setCookieAuthVerified(true);
      clearExplicitLogout();

      // Redirect to /home after authentication for Google Ads conversion tracking
      window.dispatchEvent(new Event('storage'));
      window.dispatchEvent(new Event('authChanged'));
      
      // Trigger Google Ads conversion tracking for sign-in
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'conversion', {
          'send_to': 'AW-17867073014',
          'event_category': 'authentication',
          'event_label': 'sign_in_completed'
        });
      }
      
      // Redirect to /home and clean URL
      if (window.location.pathname === '/') {
        window.location.href = '/home';
      } else {
        window.history.replaceState(null, '', window.location.pathname);
      }
    }

    checkAuth();

    const timeout = setTimeout(() => {
      checkAuth();
    }, 200);

    const handleStorageChange = () => {
      checkAuth();
    };

    const handleAuthChange = () => {
      checkAuth();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('authChanged', handleAuthChange);
    window.addEventListener('authHydrated', handleAuthChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('authChanged', handleAuthChange);
      window.removeEventListener('authHydrated', handleAuthChange);
      clearTimeout(timeout);
    };
  }, []);

  // Trial cooldown timer - check when trial ended and calculate remaining cooldown
  useEffect(() => {
    const checkCooldown = () => {
      const lastEndRaw = localStorage.getItem(TRIAL_LAST_END_KEY);
      const lastEnd = lastEndRaw ? Number(lastEndRaw) : 0;
      if (!Number.isNaN(lastEnd) && lastEnd > 0) {
        const elapsed = Date.now() - lastEnd;
        if (elapsed < TRIAL_COOLDOWN_MS) {
          setTrialCooldownRemainingMs(TRIAL_COOLDOWN_MS - elapsed);
        } else {
          setTrialCooldownRemainingMs(0);
        }
      } else {
        setTrialCooldownRemainingMs(0);
      }
    };

    checkCooldown();
    const intervalId = setInterval(checkCooldown, 1000);
    return () => clearInterval(intervalId);
  }, [TRIAL_COOLDOWN_MS]);

  // Check for active trial session and update timer
  // Runs on mount AND when isAuth changes (user logs in/out)
  useEffect(() => {
    const checkTrialSession = () => {
      const sessionEndTime = localStorage.getItem('session_end_time');
      const interviewConfig = localStorage.getItem('interview_config');
      
      // Check if there's an active trial session
      if (sessionEndTime && interviewConfig) {
        try {
          const config = JSON.parse(interviewConfig);
          if (config.isTrial === true) {
            const remaining = Math.floor((parseInt(sessionEndTime) - Date.now()) / 1000);
            if (remaining > 0) {
              setTrialTimeRemaining(remaining);
              return true;
            } else {
              // Trial has ended
              setTrialTimeRemaining(null);
              localStorage.removeItem('session_end_time');
            }
          }
        } catch (e) {
          // Invalid config
        }
      }
      setTrialTimeRemaining(null);
      return false;
    };

    // Check immediately
    checkTrialSession();

    // Set up interval to update timer every second
    const intervalId = setInterval(() => {
      checkTrialSession();
    }, 1000);

    // Listen for storage changes and trial restoration
    const handleStorageChange = () => {
      checkTrialSession();
    };
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('trialSessionRestored', handleStorageChange);
    window.addEventListener('authChanged', handleStorageChange);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('trialSessionRestored', handleStorageChange);
      window.removeEventListener('authChanged', handleStorageChange);
    };
  }, [isAuth]); // Re-run when auth state changes

  // Format time for display (mm:ss)
  const formatTrialTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Format cooldown time (mm:ss)
  const formatCooldownTime = (ms) => {
    const totalSeconds = Math.ceil(ms / 1000);
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const isTrialCooldownActive = trialCooldownRemainingMs > 0;

  useEffect(() => {
    if (videoLoaded) return;
    if (!wrapperRef.current) return;
    if (!('IntersectionObserver' in window)) return;

    const obs = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setVideoLoaded(true);
          obs.disconnect();
        }
      }); 
    }, { threshold: 0.25 });

    obs.observe(wrapperRef.current);
    return () => obs.disconnect();
  }, [videoLoaded]);

  const checkCredits = async () => {
    try {
      const data = await fetchCreditsCached();
      const totalCredits = data.totalCredits || 0;
      return totalCredits > 0;
    } catch (error) {
      console.error('Error checking credits:', error);
      return false;
    }
  };

  const handleStartSession = async () => {
    if (!isAuth) {
      navigate('/signup');
      return;
    }

    const hasCredits = await checkCredits();

    if (!hasCredits) {
      setShowCreditsModal(true);
    } else {
      setIsTrial(false);
      setShowTrialModal(true);
    }
  };

  const handleStartTrial = () => {
    if (!isAuth) {
      navigate('/signup');
      return;
    }
    setIsTrial(true);
    setShowTrialModal(true);
  };

  return (
    <div className="welcome-container">
      <SEO 
        title="BlackKiteAI - AI-Powered Interview Preparation & Practice Tool"
        description="Master your interview answers with AI-powered coaching. BlackKiteAI helps you prepare compelling, resume-specific answers to any interview question. Practice technical and behavioral questions before your big day."
        keywords="interview preparation, interview practice, AI interview coach, interview training, mock interview, technical interview prep, behavioral interview prep, resume-based coaching, job interview preparation, AI interview preparation"
        canonical="https://www.blackkiteai.com/"
      />
      <CreditsModal 
        isOpen={showCreditsModal} 
        onClose={() => setShowCreditsModal(false)} 
      />
      <StartTrialModal 
        isOpen={showTrialModal} 
        onClose={() => setShowTrialModal(false)}
        isTrial={isTrial}
      />
      
      <div className="welcome-header">
        <h1 className="welcome-title">
          <img
            src={`${process.env.PUBLIC_URL || ''}/bkai_logo.jpeg`}
            alt="BlackKiteAI logo"
            className="welcome-logo-img"
            width="512"
            height="512"
          />
          <span className="welcome-title-text">
            <span className="welcome-title-base">Black</span>
            <span className="welcome-title-accent">KiteAI</span>
          </span>
        </h1>
        <div className="header-actions">
          {isAuth ? (
            <>
              <span className="user-chip">
                Hi, {userInfo?.name || userInfo?.email?.split('@')[0] || 'User'}
              </span>
              {trialTimeRemaining !== null ? (
                <button 
                  className="btn-trial-header btn-trial-active" 
                  onClick={() => navigate('/chat')}
                  style={{
                    background: 'linear-gradient(135deg, rgba(72, 208, 42, 0.2), rgba(72, 208, 42, 0.1))',
                    border: '2px solid #48d02a',
                    animation: 'pulse 2s ease-in-out infinite'
                  }}
                >
                  ⏱ Trial: {formatTrialTime(trialTimeRemaining)}
                </button>
              ) : isTrialCooldownActive ? (
                <button 
                  className="btn-trial-header btn-trial-disabled" 
                  disabled
                  style={{
                    opacity: 0.6,
                    cursor: 'not-allowed',
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '2px solid rgba(255, 255, 255, 0.1)'
                  }}
                >
                  ⏳ Trial available in {formatCooldownTime(trialCooldownRemainingMs)}
                </button>
              ) : (
                <button className="btn-trial-header" onClick={handleStartTrial}>
                  Start Trial
                </button>
              )}
              <button className="btn-start-header" onClick={handleStartSession}>
                Start Session
              </button>
            </>
          ) : (
            <button className="btn-start-header" onClick={() => navigate('/signup')}>
              Sign In
            </button>
          )}
        </div>
      </div>

      <div className="welcome-content">
        <section className="hero-section">
          <div className="hero-copy">
            <p className="eyebrow">✨ Your Real-Time AI Sparkles</p>
            <h2>AI Interview Assistant</h2>
            <p className="lede">
              Practice and prepare for job interviews with AI-powered assistance. Get intelligent answer suggestions for interview questions in real-time. <strong>Secure and private.</strong>
            </p>
            <p className="lede" style={{ marginTop: '1rem' }}>
              Available on both desktop and web platforms
            </p>
          </div>

          <div className="hero-video">
            <iframe
              title="BlackKiteAI demo"
              src={`https://www.youtube.com/embed/${VIDEO_ID}?rel=0`}
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              loading="lazy"
            ></iframe>
          </div>
        </section>

        <section className="hero-section hero-section-demo">
          <div className="hero-side-card">
            <p className="eyebrow">Desktop Demo</p>
            <h4>See BlackKiteAI in action</h4>
            <div className="demo-video-container">
              <iframe
                title="BlackKiteAI Desktop Demo"
                src="https://www.youtube.com/embed/cuheiT2HbKc?rel=0"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                loading="lazy"
              ></iframe>
            </div>
          </div>

          {/* Referral Promo Card */}
          <div className="referral-hero-card">
            <div className="referral-hero-glow"></div>
            <div className="referral-hero-badge">FREE CREDITS</div>
            <div className="referral-hero-gift">🎁</div>
            <h4 className="referral-hero-title">Refer &amp; Earn</h4>
            <p className="referral-hero-subtitle">
              Share with friends.<br />
              Earn <strong>1 free credit</strong> per purchase.
            </p>
            <div className="referral-hero-visual">
              <div className="referral-visual-row">
                <div className="referral-visual-step">
                  <span className="rv-icon">📤</span>
                  <span className="rv-label">Share Link</span>
                </div>
                <div className="referral-visual-arrow">→</div>
                <div className="referral-visual-step">
                  <span className="rv-icon">👤</span>
                  <span className="rv-label">Friend Joins</span>
                </div>
                <div className="referral-visual-arrow">→</div>
                <div className="referral-visual-step">
                  <span className="rv-icon">🎉</span>
                  <span className="rv-label">You Earn</span>
                </div>
              </div>
            </div>
            <div className="referral-hero-stats">
              <div className="referral-stat">
                <span className="referral-stat-value">∞</span>
                <span className="referral-stat-label">No Limit</span>
              </div>
              <div className="referral-stat">
                <span className="referral-stat-value">1</span>
                <span className="referral-stat-label">Credit / Referral</span>
              </div>
              <div className="referral-stat">
                <span className="referral-stat-value">0₹</span>
                <span className="referral-stat-label">Cost to You</span>
              </div>
            </div>
            <button 
              className="referral-hero-btn" 
              onClick={() => isAuth ? navigate('/referral-history') : navigate('/signup')}
            >
              {isAuth ? 'Get My Referral Link' : 'Sign Up & Start Earning'}
            </button>
          </div>
        </section>

        {/* Trusted By Section */}
        <section style={{ padding: '40px 0', background: 'rgba(15, 26, 46, 0.2)', borderTop: '1px solid var(--border-color)', borderBottom: '1px solid var(--border-color)' }}>
          <div className="container">
            <p className="trusted-by-heading">Trusted by candidates interviewing at</p>
            <div className="logos-scroll-container">
              <div className="logos-scroll-wrapper">
                {/* First set of logos */}
                <div className="company-logo">
                  <img src="https://www.google.com/favicon.ico" alt="Google" style={{ width: '32px', height: '32px' }} />
                  <span>Google</span>
                </div>
                <div className="company-logo">
                  <img src="https://www.amazon.com/favicon.ico" alt="Amazon" style={{ width: '32px', height: '32px' }} />
                  <span>Amazon</span>
                </div>
                <div className="company-logo">
                  <img src="https://www.microsoft.com/favicon.ico" alt="Microsoft" style={{ width: '32px', height: '32px' }} />
                  <span>Microsoft</span>
                </div>
                <div className="company-logo">
                  <img src="https://www.facebook.com/favicon.ico" alt="Meta" style={{ width: '32px', height: '32px' }} />
                  <span>Meta</span>
                </div>
                <div className="company-logo">
                  <img src="https://www.apple.com/favicon.ico" alt="Apple" style={{ width: '32px', height: '32px' }} />
                  <span>Apple</span>
                </div>
                <div className="company-logo">
                  <img src="https://www.netflix.com/favicon.ico" alt="Netflix" style={{ width: '32px', height: '32px' }} />
                  <span>Netflix</span>
                </div>
                <div className="company-logo">
                  <img src="https://www.tesla.com/favicon.ico" alt="Tesla" style={{ width: '32px', height: '32px' }} />
                  <span>Tesla</span>
                </div>
                <div className="company-logo">
                  <img src="https://www.uber.com/favicon.ico" alt="Uber" style={{ width: '32px', height: '32px' }} />
                  <span>Uber</span>
                </div>
                <div className="company-logo">
                  <img src="https://www.airbnb.com/favicon.ico" alt="Airbnb" style={{ width: '32px', height: '32px' }} />
                  <span>Airbnb</span>
                </div>
                <div className="company-logo">
                  <img src="https://www.spotify.com/favicon.ico" alt="Spotify" style={{ width: '32px', height: '32px' }} />
                  <span>Spotify</span>
                </div>
                <div className="company-logo">
                  <img src="https://www.salesforce.com/favicon.ico" alt="Salesforce" style={{ width: '32px', height: '32px' }} />
                  <span>Salesforce</span>
                </div>
                <div className="company-logo">
                  <img src="https://www.oracle.com/favicon.ico" alt="Oracle" style={{ width: '32px', height: '32px' }} />
                  <span>Oracle</span>
                </div>
                {/* Duplicate set for seamless loop */}
                <div className="company-logo">
                  <img src="https://www.google.com/favicon.ico" alt="Google" style={{ width: '32px', height: '32px' }} />
                  <span>Google</span>
                </div>
                <div className="company-logo">
                  <img src="https://www.amazon.com/favicon.ico" alt="Amazon" style={{ width: '32px', height: '32px' }} />
                  <span>Amazon</span>
                </div>
                <div className="company-logo">
                  <img src="https://www.microsoft.com/favicon.ico" alt="Microsoft" style={{ width: '32px', height: '32px' }} />
                  <span>Microsoft</span>
                </div>
                <div className="company-logo">
                  <img src="https://www.facebook.com/favicon.ico" alt="Meta" style={{ width: '32px', height: '32px' }} />
                  <span>Meta</span>
                </div>
                <div className="company-logo">
                  <img src="https://www.apple.com/favicon.ico" alt="Apple" style={{ width: '32px', height: '32px' }} />
                  <span>Apple</span>
                </div>
                <div className="company-logo">
                  <img src="https://www.netflix.com/favicon.ico" alt="Netflix" style={{ width: '32px', height: '32px' }} />
                  <span>Netflix</span>
                </div>
                <div className="company-logo">
                  <img src="https://www.tesla.com/favicon.ico" alt="Tesla" style={{ width: '32px', height: '32px' }} />
                  <span>Tesla</span>
                </div>
                <div className="company-logo">
                  <img src="https://www.uber.com/favicon.ico" alt="Uber" style={{ width: '32px', height: '32px' }} />
                  <span>Uber</span>
                </div>
                <div className="company-logo">
                  <img src="https://www.airbnb.com/favicon.ico" alt="Airbnb" style={{ width: '32px', height: '32px' }} />
                  <span>Airbnb</span>
                </div>
                <div className="company-logo">
                  <img src="https://www.spotify.com/favicon.ico" alt="Spotify" style={{ width: '32px', height: '32px' }} />
                  <span>Spotify</span>
                </div>
                <div className="company-logo">
                  <img src="https://www.salesforce.com/favicon.ico" alt="Salesforce" style={{ width: '32px', height: '32px' }} />
                  <span>Salesforce</span>
                </div>
                <div className="company-logo">
                  <img src="https://www.oracle.com/favicon.ico" alt="Oracle" style={{ width: '32px', height: '32px' }} />
                  <span>Oracle</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Privacy Demo Videos Section */}
        <section className="pdv-section">
          <h2 className="pdv-title">BlackKite AI Support</h2>

          <div className="pdv-grid">
            {[
              {
                name: "Zoom",
                status: "Undetectable",
                icon: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='white'%3E%3Cpath d='M2 8v8a2 2 0 0 0 2 2h12v-4l6 4V6l-6 4V6H4a2 2 0 0 0-2 2z'/%3E%3C/svg%3E",
                bg: "#2b76ff",
              },
              {
                name: "Microsoft Teams",
                status: "Undetectable",
                icon: "/teams-svg.svg",
                bg: "#5b5fc7",
                className: "teams",
              },
              {
                name: "Google Meet",
                status: "Undetectable",
                icon: "/google_meet.svg",
                bg: "white",
                className: "meet",
              },
              {
                name: "Webex",
                status: "Undetectable",
                icon: "/webex.svg",
                bg: "#00a0dc",
                className: "webex",
              },
              {
                name: "HackerRank",
                status: "Undetectable",
                icon: "/hackerrank.svg",
                bg: "#1ba94c",
                className: "hackerrank",
              },
            ].map((it) => (
              <a key={it.name} className="pdv-card" href="#" aria-label={it.name}>
                <div className="pdv-left">
                  <div className={`pdv-icon ${it.className || ''}`} style={{ background: it.bg }}>
                    <img src={it.icon} alt={it.name} width={36} height={36} />
                  </div>

                  <div className="pdv-copy">
                    <div className="pdv-name">{it.name}</div>
                    <div className="pdv-sub">
                      <span className="pdv-dot" />
                      <span>{it.status}</span>
                    </div>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </section>

        <section className="hero-section hero-section-secondary">
          <div className="hero-side-card">
            <p className="eyebrow">Website Demo</p>
            <h4>Explore BlackKiteAI on the web</h4>
            <p style={{ marginTop: '1rem', marginBottom: '0.5rem' }}>Watch a quick demo of our web interface:</p>
            <a 
              href="https://www.youtube.com/watch?v=NKdwL4-Lilw" 
              target="_blank" 
              rel="noopener noreferrer"
              className="youtube-link"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: '8px' }}>
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
              Watch on YouTube
            </a>
          </div>
        </section>

        <section className="section languages-section">
          <div className="section-heading">
            <p className="eyebrow">Global Support</p>
            <h3>AI speech to text transcription in 55+ languages</h3>
          </div>
          <div className="languages-marquee">
            <div className="languages-track">
              <span className="language-item"><SA className="flag-icon" /> Arabic</span>
              <span className="language-item"><RU className="flag-icon" /> Bashkir</span>
              <span className="language-item"><ES className="flag-icon" /> Basque</span>
              <span className="language-item"><BY className="flag-icon" /> Belarusian</span>
              <span className="language-item"><BD className="flag-icon" /> Bengali</span>
              <span className="language-item"><BG className="flag-icon" /> Bulgarian</span>
              <span className="language-item"><HK className="flag-icon" /> Cantonese</span>
              <span className="language-item"><ES className="flag-icon" /> Catalan</span>
              <span className="language-item"><HR className="flag-icon" /> Croatian</span>
              <span className="language-item"><CZ className="flag-icon" /> Czech</span>
              <span className="language-item"><DK className="flag-icon" /> Danish</span>
              <span className="language-item"><NL className="flag-icon" /> Dutch</span>
              <span className="language-item"><GB className="flag-icon" /> English</span>
              <span className="language-item"><span className="flag-icon world-icon">🌐</span> Esperanto</span>
              <span className="language-item"><EE className="flag-icon" /> Estonian</span>
              <span className="language-item"><FI className="flag-icon" /> Finnish</span>
              <span className="language-item"><FR className="flag-icon" /> French</span>
              <span className="language-item"><ES className="flag-icon" /> Galician</span>
              <span className="language-item"><DE className="flag-icon" /> German</span>
              <span className="language-item"><GR className="flag-icon" /> Greek</span>
              <span className="language-item"><IL className="flag-icon" /> Hebrew</span>
              <span className="language-item"><IN className="flag-icon" /> Hindi</span>
              <span className="language-item"><HU className="flag-icon" /> Hungarian</span>
              <span className="language-item"><span className="flag-icon world-icon">🌐</span> Interlingua</span>
              <span className="language-item"><IE className="flag-icon" /> Irish</span>
              <span className="language-item"><IT className="flag-icon" /> Italian</span>
              <span className="language-item"><ID className="flag-icon" /> Indonesian</span>
              <span className="language-item"><JP className="flag-icon" /> Japanese</span>
              <span className="language-item"><KR className="flag-icon" /> Korean</span>
              <span className="language-item"><LV className="flag-icon" /> Latvian</span>
              <span className="language-item"><LT className="flag-icon" /> Lithuanian</span>
              <span className="language-item"><MY className="flag-icon" /> Malay</span>
              <span className="language-item"><MT className="flag-icon" /> Maltese</span>
              <span className="language-item"><CN className="flag-icon" /> Mandarin</span>
              <span className="language-item"><IN className="flag-icon" /> Marathi</span>
              <span className="language-item"><MN className="flag-icon" /> Mongolian</span>
              <span className="language-item"><NO className="flag-icon" /> Norwegian</span>
              <span className="language-item"><IR className="flag-icon" /> Persian</span>
              <span className="language-item"><PL className="flag-icon" /> Polish</span>
              <span className="language-item"><PT className="flag-icon" /> Portuguese</span>
              <span className="language-item"><RO className="flag-icon" /> Romanian</span>
              <span className="language-item"><RU className="flag-icon" /> Russian</span>
              <span className="language-item"><SK className="flag-icon" /> Slovakian</span>
              <span className="language-item"><SI className="flag-icon" /> Slovenian</span>
              <span className="language-item"><ES className="flag-icon" /> Spanish</span>
              <span className="language-item"><KE className="flag-icon" /> Swahili</span>
              <span className="language-item"><SE className="flag-icon" /> Swedish</span>
              <span className="language-item"><IN className="flag-icon" /> Tamil</span>
              <span className="language-item"><PH className="flag-icon" /> Tagalog</span>
              <span className="language-item"><TH className="flag-icon" /> Thai</span>
              <span className="language-item"><TR className="flag-icon" /> Turkish</span>
              <span className="language-item"><PK className="flag-icon" /> Urdu</span>
              <span className="language-item"><CN className="flag-icon" /> Uyghur</span>
              <span className="language-item"><UA className="flag-icon" /> Ukrainian</span>
              <span className="language-item"><VN className="flag-icon" /> Vietnamese</span>
              <span className="language-item"><WelshFlag className="flag-icon" /> Welsh</span>
              {/* Duplicate for seamless loop */}
              <span className="language-item"><SA className="flag-icon" /> Arabic</span>
              <span className="language-item"><RU className="flag-icon" /> Bashkir</span>
              <span className="language-item"><ES className="flag-icon" /> Basque</span>
              <span className="language-item"><BY className="flag-icon" /> Belarusian</span>
              <span className="language-item"><BD className="flag-icon" /> Bengali</span>
              <span className="language-item"><BG className="flag-icon" /> Bulgarian</span>
              <span className="language-item"><HK className="flag-icon" /> Cantonese</span>
              <span className="language-item"><ES className="flag-icon" /> Catalan</span>
              <span className="language-item"><HR className="flag-icon" /> Croatian</span>
              <span className="language-item"><CZ className="flag-icon" /> Czech</span>
              <span className="language-item"><DK className="flag-icon" /> Danish</span>
              <span className="language-item"><NL className="flag-icon" /> Dutch</span>
              <span className="language-item"><GB className="flag-icon" /> English</span>
              <span className="language-item"><span className="flag-icon world-icon">🌐</span> Esperanto</span>
              <span className="language-item"><EE className="flag-icon" /> Estonian</span>
              <span className="language-item"><FI className="flag-icon" /> Finnish</span>
              <span className="language-item"><FR className="flag-icon" /> French</span>
              <span className="language-item"><ES className="flag-icon" /> Galician</span>
              <span className="language-item"><DE className="flag-icon" /> German</span>
              <span className="language-item"><GR className="flag-icon" /> Greek</span>
              <span className="language-item"><IL className="flag-icon" /> Hebrew</span>
              <span className="language-item"><IN className="flag-icon" /> Hindi</span>
              <span className="language-item"><HU className="flag-icon" /> Hungarian</span>
              <span className="language-item"><span className="flag-icon world-icon">🌐</span> Interlingua</span>
              <span className="language-item"><IE className="flag-icon" /> Irish</span>
              <span className="language-item"><IT className="flag-icon" /> Italian</span>
              <span className="language-item"><ID className="flag-icon" /> Indonesian</span>
              <span className="language-item"><JP className="flag-icon" /> Japanese</span>
              <span className="language-item"><KR className="flag-icon" /> Korean</span>
              <span className="language-item"><LV className="flag-icon" /> Latvian</span>
              <span className="language-item"><LT className="flag-icon" /> Lithuanian</span>
              <span className="language-item"><MY className="flag-icon" /> Malay</span>
              <span className="language-item"><MT className="flag-icon" /> Maltese</span>
              <span className="language-item"><CN className="flag-icon" /> Mandarin</span>
              <span className="language-item"><IN className="flag-icon" /> Marathi</span>
              <span className="language-item"><MN className="flag-icon" /> Mongolian</span>
              <span className="language-item"><NO className="flag-icon" /> Norwegian</span>
              <span className="language-item"><IR className="flag-icon" /> Persian</span>
              <span className="language-item"><PL className="flag-icon" /> Polish</span>
              <span className="language-item"><PT className="flag-icon" /> Portuguese</span>
              <span className="language-item"><RO className="flag-icon" /> Romanian</span>
              <span className="language-item"><RU className="flag-icon" /> Russian</span>
              <span className="language-item"><SK className="flag-icon" /> Slovakian</span>
              <span className="language-item"><SI className="flag-icon" /> Slovenian</span>
              <span className="language-item"><ES className="flag-icon" /> Spanish</span>
              <span className="language-item"><KE className="flag-icon" /> Swahili</span>
              <span className="language-item"><SE className="flag-icon" /> Swedish</span>
              <span className="language-item"><IN className="flag-icon" /> Tamil</span>
              <span className="language-item"><PH className="flag-icon" /> Tagalog</span>
              <span className="language-item"><TH className="flag-icon" /> Thai</span>
              <span className="language-item"><TR className="flag-icon" /> Turkish</span>
              <span className="language-item"><PK className="flag-icon" /> Urdu</span>
              <span className="language-item"><CN className="flag-icon" /> Uyghur</span>
              <span className="language-item"><UA className="flag-icon" /> Ukrainian</span>
              <span className="language-item"><VN className="flag-icon" /> Vietnamese</span>
              <span className="language-item"><WelshFlag className="flag-icon" /> Welsh</span>
            </div>
          </div>
        </section>

        {/* AI Features Section */}
        <section className="ai-features-section">
          <div className="section-heading">
            <h2 className="ai-features-title">Powerful AI Features</h2>
          </div>
          <div className="ai-features-grid">
            <div className="ai-feature-card">
              <div className="ai-feature-badge">RESUME</div>
              <h3 className="ai-feature-title">Upload your Resume</h3>
              <div className="ai-feature-visual resume-visual">
                <div className="resume-icons">
                  <div className="resume-icon">📄</div>
                  <div className="resume-icon">📄</div>
                  <div className="resume-icon">📄</div>
                </div>
                <div className="ai-analyzing-badge">🤖 AI Analyzing...</div>
              </div>
              <p className="ai-feature-description">
                Upload multiple resumes and let AI analyze your experience to generate perfectly matched interview answers.
              </p>
            </div>

            <div className="ai-feature-card">
              <div className="ai-feature-badge instant">INSTANT ANSWERS</div>
              <h3 className="ai-feature-title">Auto Generate</h3>
              <div className="ai-feature-visual chat-visual">
                <div className="chat-bubble interviewer">
                  <p>Tell me about a challenging project you've worked on. <span className="highlight-text">How did you overcome the obstacles?</span></p>
                </div>
                <div className="ai-avatar">🎯</div>
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
              <p className="ai-feature-description">
                AI instantly analyzes questions and generates perfect answers in real-time. Never struggle with tough interview questions again.
              </p>
            </div>
          </div>
        </section>

        <section className="hero-section hero-section-secondary">
          <div className="hero-side-card">
            <p className="eyebrow">Practice interface</p>
            <h4>Everything you need for effective prep</h4>
            <ul className="side-list">
              <li>Type or paste any interview question</li>
              <li>Get AI-generated answers based on your resume</li>
              <li>Review relevant experience and accomplishments</li>
              <li>Edit, save, and practice your responses</li>
            </ul>
          </div>
        </section>

        <section className="section">
          <div className="section-heading">
            <p className="eyebrow">What matters</p>
            <h3>Why candidates choose BlackKiteAI for interview prep</h3>
          </div>
          <ul className="point-grid">
            {highlights.map((item) => (
              <li key={item.title} className="point-item">
                <h4>{item.title}</h4>
                <p>{item.text}</p>
              </li>
            ))}
          </ul>
        </section>

        <section className="section">
          <div className="section-heading">
            <p className="eyebrow">How it works</p>
            <h3>Three quick steps</h3>
          </div>
          <div className="steps-grid">
            {steps.map((step, idx) => (
              <div key={step.title} className="step-card">
                <div className="step-pill">Step {idx + 1}</div>
                <div>
                  <h4>{step.title}</h4>
                  <p>{step.text}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="section cta-section">
          <div className="cta-card">
            <div>
              <p className="eyebrow">Start preparing today</p>
              <h3>Walk into every interview confident, prepared, and ready to succeed.</h3>
              <ul className="mini-list">
                <li>Personalized to your background</li>
                <li>Practice anytime, anywhere</li>
                <li>Private and secure</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Referral Program Section */}
        <section className="referral-promo-section">
          <div className="referral-promo-container">
            <div className="referral-promo-content">
              <div className="referral-promo-icon">🎁</div>
              <h3 className="referral-promo-title">Earn Free Credits with Referrals</h3>
              <p className="referral-promo-text">
                Share your referral link with friends. When they make a purchase, you earn <strong>1 free credit</strong> — no limits on how many you can earn!
              </p>
              <div className="referral-promo-steps">
                <div className="referral-promo-step">
                  <span className="referral-step-num">1</span>
                  <span>Get your unique referral link</span>
                </div>
                <div className="referral-promo-step">
                  <span className="referral-step-num">2</span>
                  <span>Share with friends via WhatsApp or email</span>
                </div>
                <div className="referral-promo-step">
                  <span className="referral-step-num">3</span>
                  <span>Earn 1 free credit per successful purchase</span>
                </div>
              </div>
              {isAuth ? (
                <button className="referral-promo-btn" onClick={() => navigate('/referral-history')}>
                  View My Referral Program →
                </button>
              ) : (
                <button className="referral-promo-btn" onClick={() => navigate('/signup')}>
                  Sign Up & Start Earning →
                </button>
              )}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="faq-section">
          <div className="container">
            <div className="faq-container">
              <div className="faq-header">
                <h2>Frequently Asked Questions</h2>
                <p>Everything you need to know about BlackKiteAI</p>
              </div>
              
              <div className="faq-list">
                <FAQItem 
                  question="How does BlackKiteAI work?"
                  answer="BlackKiteAI uses advanced AI technology to listen to interview questions in real-time and provide intelligent, contextual answers instantly. Simply start the app, join your interview, and our AI will assist you throughout the conversation."
                />
                <FAQItem 
                  question="Is BlackKiteAI compatible with all interview platforms?"
                  answer="Yes! BlackKiteAI works with all major video conferencing platforms including Zoom, Microsoft Teams, Google Meet, and more. Our desktop app captures audio system-wide, making it platform-agnostic."
                />
                <FAQItem 
                  question="What languages are supported?"
                  answer="BlackKiteAI supports 52+ languages including English, Spanish, French, German, Chinese, Japanese, Hindi, and many more. Our AI can understand questions and provide answers in your preferred language."
                />
                <FAQItem 
                  question="How do credits work?"
                  answer="Credits are used each time you activate the AI during an interview. Each credit provides 60 minutes (1 hour) of AI assistance. For shorter sessions, you can use 0.5 credits for 30 minutes. You can purchase credit packages based on your needs, and unused credits never expire."
                />
                <FAQItem 
                  question="Is my data secure and private?"
                  answer="Absolutely. We take privacy seriously. All audio processing happens securely, and we don't store your interview recordings. Your data is encrypted end-to-end and we comply with all major privacy regulations including GDPR and CCPA."
                />
                <FAQItem 
                  question="Can I use this for practice interviews?"
                  answer="Yes! BlackKiteAI is an excellent tool for interview preparation and practice sessions. Use it to improve your responses, build confidence, and familiarize yourself with common interview questions in your field."
                />
                <FAQItem 
                  question="Where can I buy credits?"
                  answer={
                    <>
                      You can purchase credits by visiting <a href="https://blackkiteai.com/buy-credits" className="faq-link">https://blackkiteai.com</a>. Choose from our affordable pricing plans and get started with your interview preparation today!
                    </>
                  }
                />
                <FAQItem 
                  question="Which is better: Desktop App or Web App?"
                  answer="The Desktop App is recommended for the best experience. It offers superior audio capture, lower latency, better performance, and more reliable system-level integration compared to the web version. Download our desktop application for Windows to get the most out of BlackKiteAI's features."
                />
              </div>
            </div>
          </div>
        </section>

        {/* Important Information Section */}
        <section className="important-info-section">
          <div className="important-info-container">
            <h2 className="important-info-title">Important Information</h2>
            <p className="important-info-text">
              BlackKiteAI is an AI-powered interview preparation and assistance tool designed to help candidates practice and improve their interview skills. Users are responsible for using this tool in accordance with the policies and guidelines of their interview platforms and prospective employers. We recommend transparency with potential employers about preparation tools used.
            </p>
            <p className="important-info-text">
              Results may vary based on individual usage, interview context, and other factors. This service is provided for educational and preparation purposes. By using BlackKiteAI, you agree to our <a href="/terms" className="important-info-link">Terms of Service</a> and <a href="/privacy" className="important-info-link">Privacy Policy</a>.
            </p>
          </div>
        </section>

        {/* Footer Section */}
        <footer className="footer-section">
          <div className="footer-container">
            <div className="footer-column">
              <div className="footer-logo">
                <img src="/bkai_logo.jpeg" alt="BlackKiteAI" width="48" height="48" />
                <h3>BlackKiteAI</h3>
              </div>
              <p className="footer-description">
                Real-Time AI Interview Assistant. Get instant, intelligent answers during your interviews with our cutting-edge AI technology.
              </p>
            </div>

            <div className="footer-column">
              <h4 className="footer-heading">Contact</h4>
              <a href="mailto:support@blackkiteai.com" className="footer-link footer-email">
                support@blackkiteai.com
              </a>
            </div>

            <div className="footer-column">
              <h4 className="footer-heading">Legal</h4>
              <a href="/terms" className="footer-link">Terms & Conditions</a>
              <a href="/privacy" className="footer-link">Privacy Policy</a>
            </div>

            <div className="footer-column">
              <h4 className="footer-heading">Follow Us</h4>
              <div className="footer-social">
                <a href="https://twitter.com/blackkiteai" target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="Twitter">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                </a>
                <a href="https://instagram.com/blackkiteai" target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="Instagram">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </a>
                <a href="https://www.linkedin.com/in/black-kite-ai-2343123a3/" target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="LinkedIn">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </a>
                <a href="https://www.youtube.com/@Blackkite-ai" target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="YouTube">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>
          
          <div className="footer-copyright">
            © 2026 BlackKiteAI. All rights reserved.
          </div>
        </footer>
      </div>
    </div>
  );
}

// FAQ Item Component
function FAQItem({ question, answer }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="faq-item">
      <button 
        className="faq-question" 
        aria-expanded={isOpen}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span>{question}</span>
        <span className={`faq-icon ${isOpen ? 'open' : ''}`}>+</span>
      </button>
      <div className={`faq-answer ${isOpen ? 'open' : ''}`}>
        <div className="faq-answer-content">
          {answer}
        </div>
      </div>
    </div>
  );
}

export default Welcome;
